import { useState, useEffect, useCallback } from 'react';
import useMatchMedia from '../../../../../utils/hooks/useMatchMedia';
import { DS_breakpoints as coreBreakpoints } from '../../../../base/theme/DS_Tokens/core';

export const useVirtualLocationScroll = (
  scrollRef,
  items,
  itemsPerPage = 6,
  selectedItem,
  isBookingFunnel,
  isGeoSearched,
) => {
  const [displayedItems, setDisplayedItems] = useState([]);
  const [renderCount, setRenderCount] = useState(itemsPerPage);
  const [displayedItemCodes, setDisplayedItemCodes] = useState(new Set());
  const [scrolled, setScrolled] = useState(false);
  const [allItemsDisplayed, setAllItemsDisplayed] = useState(false);

  const isMobile = useMatchMedia(`(max-width:${coreBreakpoints['lg']}rem)`);

  useEffect(() => {
    // when displayed items are updated, update the set of item codes
    const codes = displayedItems.map(item => item.code);
    setDisplayedItemCodes(new Set(codes));
  }, [displayedItems]);

  // when the user scrolls to the bottom of the container
  const loadMoreItems = useCallback(() => {
    setRenderCount(prevRenderCount => {
      const newRenderCount = prevRenderCount + itemsPerPage;

      // check if the new render count is greater than the total number of items
      if (newRenderCount >= items.length || isGeoSearched) {
        // stop loading more items if we're at the end
        setRenderCount(items.length);
        setAllItemsDisplayed(true);
      }
      return newRenderCount <= items.length ? newRenderCount : items.length;
    });
  }, [itemsPerPage, items]);

  // update the displayed items when renderCount changes
  useEffect(() => {
    if (items.length === 1) setAllItemsDisplayed(true);
    if (isGeoSearched) return;

    const newDisplayedItems = items.slice(0, renderCount);
    setDisplayedItems(newDisplayedItems);
  }, [renderCount, items]);

  // handler for scrolling event
  const handleScroll = () => {
    if (isGeoSearched && !isMobile) return;
    const list = scrollRef.current;

    if (list.scrollTop > 10) setScrolled(true);
    else if (list.scrollTop === 1) setScrolled(true);
    else setScrolled(false);
    if (isMobile && isBookingFunnel) {
      if (isGeoSearched && isMobile) setAllItemsDisplayed(true);
      const reachedBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

      if ((renderCount >= items.length && reachedBottom) || (isGeoSearched && reachedBottom)) {
        //  reached the bottom of the container
        document.querySelector('footer').style.bottom = 0;
        document.querySelector('footer').style.zIndex = 100;
        document.querySelector('footer').style.position = 'fixed';
        document.querySelector('footer').style.width = '100%';
        const discountAsterisk = document.querySelector('.discount-asterisk');
        if (discountAsterisk) {
          discountAsterisk.style.bottom = `133px`;
          discountAsterisk.style.zIndex = 100;
          discountAsterisk.style.position = 'fixed';
          discountAsterisk.style.width = '100%';
        }

        // store the scrollTop when the bottom is reached
        list.dataset.bottomReachedScrollTop = list.scrollTop;
      }

      const bottomReachedScrollTop = parseFloat(list.dataset.bottomReachedScrollTop || '-1');

      if (bottomReachedScrollTop >= 0 && list.scrollTop <= bottomReachedScrollTop - 100) {
        // reached the bottom of the container and then you scroll up 100px
        document.querySelector('footer').style.position = 'relative';
        document.querySelector('footer').style.zIndex = 1;
        const discountAsterisk = document.querySelector('.discount-asterisk');
        if (discountAsterisk) {
          discountAsterisk.style.position = 'relative';
          discountAsterisk.style.zIndex = 1;
          discountAsterisk.style.bottom = 'unset';
        }

        // reset the bottom reached scrollTop
        list.dataset.bottomReachedScrollTop = -1;
      }
    }

    if (list && list.scrollTop + list.clientHeight + 5 >= list.scrollHeight) loadMoreItems();
  };

  useEffect(() => {
    const list = scrollRef.current;

    const handleScrollEvent = () => handleScroll();

    if (list)
      // only attach if geoSearch hasn't been performed
      list.addEventListener('scroll', handleScrollEvent);

    // clean up by removing the event listener, regardless of isGeoSearched
    return () => {
      if (list) list.removeEventListener('scroll', handleScrollEvent);
    };
  }, [scrollRef, handleScroll, renderCount]);

  // optionally handle the selectedItem, if it's passed
  useEffect(() => {
    if (selectedItem)
      setDisplayedItems(prevDisplayedItems => {
        const isAlreadyDisplayed = prevDisplayedItems.some(item => item.id === selectedItem.id);

        if (!isAlreadyDisplayed)
          return [selectedItem, ...items.slice(0, renderCount).filter(item => item.id !== selectedItem.id)];

        return prevDisplayedItems;
      });
  }, [selectedItem, items, renderCount]);

  return [displayedItems, displayedItemCodes, scrolled, allItemsDisplayed];
};
